import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { poets } from '../../../mocks/poets';

interface LiteraryQuotesProps {
  language: 'de' | 'ru';
}

export default function LiteraryQuotes({ language }: LiteraryQuotesProps) {
  const sectionAnim = useScrollAnimation();

  const content = {
    de: {
      title: 'Literarische Inspiration',
      subtitle: 'Große Dichter, die Peters Werk prägen',
      readMore: 'Mehr erfahren'
    },
    ru: {
      title: 'Литературное вдохновение',
      subtitle: 'Великие поэты, которые формируют творчество Питера',
      readMore: 'Узнать больше'
    }
  };

  const t = content[language];

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.6, ease: 'easeOut' } 
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-[#E8E8D0] to-[#F5F5DC]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={sectionAnim.ref}
          initial="hidden"
          animate={sectionAnim.isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-5xl text-[#1B3A5F] mb-4">
            {t.title}
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mx-auto mb-6"></div>
          <p className="text-lg text-[#1B3A5F]/80 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        <motion.div 
          initial="hidden"
          animate={sectionAnim.isVisible ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {poets.map((poet) => (
            <motion.div
              key={poet.id}
              variants={cardVariants}
              className="bg-white/80 backdrop-blur-sm rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#C5B358]/30 group cursor-pointer"
            >
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src={poet.image}
                  alt={poet.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-serif text-2xl text-white mb-1 drop-shadow-lg">
                    {poet.name}
                  </h3>
                  <p className="text-[#C5B358] text-sm font-medium drop-shadow-lg">
                    {poet.years} • {poet.nationality}
                  </p>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <i className="ri-double-quotes-l text-3xl text-[#8B1538] mb-2"></i>
                  <p className="font-serif text-[#1B3A5F] italic leading-relaxed">
                    {poet.quote[language]}
                  </p>
                </div>

                <div className="pt-4 border-t border-[#C5B358]/30">
                  <p className="text-sm text-[#1B3A5F]/70 font-serif leading-relaxed">
                    {poet.poem[language]}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
